import {
  CheckCircle2,
  Circle,
  Calendar,
  Clock,
  MoreHorizontal,
  Trash2,
  Pencil,
  Archive,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  STATUS_LABELS,
  STATUS_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  formatDueDate,
  isOverdue,
  initials,
  statusLabel,
  statusColor,
} from "@/lib/taskHelpers";

export default function TaskList({ tasks, labels, statuses, currentUser, onUpdate, onDelete, onOpen, selected, onToggleSelect }) {
  return (
    <div className="bloq-card overflow-hidden" data-testid="task-list">
      <ul className="divide-y divide-[#1a1a1f0a]">
        {tasks.map((t) => (
          <TaskRow
            key={t.task_id}
            task={t}
            statuses={statuses}
            isSelected={selected ? selected.has(t.task_id) : false}
            // When `onToggleSelect` is not provided, the checkbox slot is
            // hidden so the row layout stays exactly as it was for
            // contexts that don't support bulk select (e.g., in-meeting
            // Tasks panel).
            onToggleSelect={onToggleSelect ? () => onToggleSelect(t.task_id) : null}
            onToggle={() => onUpdate(t.task_id, { done: t.status !== "done" })}
            onArchive={() => onUpdate(t.task_id, { is_archived: !t.is_archived })}
            onDelete={() => onDelete(t.task_id)}
            onOpen={() => onOpen(t.task_id)}
          />
        ))}
      </ul>
    </div>
  );
}

function TaskRow({ task, statuses, isSelected, onToggleSelect, onToggle, onArchive, onDelete, onOpen }) {
  const isDone = task.status === "done";
  const overdue = !isDone && isOverdue(task.due_date);
  const prio = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
  // Workspace-aware: a custom status renders with its own name + color.
  // Falls back to the static map when no workspace statuses are loaded.
  const stat = statusColor(task.status, statuses);
  const sLabel = statusLabel(task.status, statuses) || STATUS_LABELS[task.status] || task.status;
  return (
    <li
      className={`flex items-center gap-3 px-4 sm:px-5 py-3.5 hover:bg-[#f5efe6]/60 transition-colors cursor-pointer group ${isSelected ? "bg-[#005e7a]/5" : ""}`}
      onClick={onOpen}
      data-testid={`task-row-${task.task_id}`}
    >
      {onToggleSelect && (
        <input
          type="checkbox"
          checked={!!isSelected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          className="accent-[#005e7a] w-4 h-4 flex-shrink-0"
          aria-label={`Select ${task.title}`}
          data-testid={`task-select-${task.task_id}`}
        />
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className="flex-shrink-0"
        aria-label={isDone ? "Mark incomplete" : "Mark complete"}
        data-testid={`task-toggle-${task.task_id}`}
      >
        {isDone ? (
          <CheckCircle2 className="w-5 h-5 text-[#16a34a]" />
        ) : (
          <Circle className="w-5 h-5 text-[#82828c] hover:text-[#005e7a]" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-medium text-sm truncate ${isDone ? "line-through text-[#82828c]" : ""}`}>
            {task.title}
          </p>
          {task.visibility === "private" && (
            <span className="text-[10px] uppercase tracking-widest text-[#82828c]">private</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-widest"
            style={{ background: stat.bg, color: stat.c }}
          >
            {sLabel}
          </span>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-widest"
            style={{ background: prio.bg, color: prio.c }}
          >
            {PRIORITY_LABELS[task.priority] || task.priority}
          </span>
          {task.due_date && (
            <span className={`inline-flex items-center gap-1 text-[11px] ${overdue ? "text-[#dc2626] font-semibold" : "text-[#82828c]"}`}>
              <Calendar className="w-3 h-3" /> {formatDueDate(task.due_date)}
            </span>
          )}
          {task.subtask_count > 0 && (
            <span className="text-[11px] text-[#82828c] inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {task.subtask_done}/{task.subtask_count} subtasks
            </span>
          )}
          {task.labels?.map((lbl) => (
            <span
              key={lbl.label_id}
              className="text-[10px] font-medium rounded-full px-2 py-0.5"
              style={{ background: `${lbl.color}1a`, color: lbl.color }}
            >
              {lbl.name}
            </span>
          ))}
        </div>
      </div>
      {task.assignee_id && (
        <Avatar className="w-7 h-7 rounded-md flex-shrink-0" title={task.assignee_name || ""}>
          <AvatarImage src={task.assignee_avatar || ""} className="rounded-md" />
          <AvatarFallback className="bg-[#005e7a] text-white text-[10px] rounded-md">{initials(task.assignee_name)}</AvatarFallback>
        </Avatar>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`task-menu-${task.task_id}`}
        >
          <MoreHorizontal className="w-4 h-4 text-[#82828c]" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            <Pencil className="w-4 h-4 mr-2" /> Open
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onArchive(); }}>
            <Archive className="w-4 h-4 mr-2" /> {task.is_archived ? "Restore" : "Archive"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-600 focus:text-red-700">
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}
